// ============================================================
// documentFontFiles.ts — the PRINT TAB side of the documents' bundled fonts
// (utils/documentFonts): the WOFF2 files in public/document-fonts, addressed
// by absolute URL on the app's own origin (the print tab is an about:blank
// document on the app's origin, so it loads them directly). Never Google. The
// server embeds the same files (backend lib/documentFontFiles); ★ keep the
// two folders identical.
//
// (batch 2) The in-app PREVIEW is a sandboxed srcdoc iframe (components/
// shared/DocumentPreviewFrame) — an OPAQUE origin, so its font requests to the
// app's origin are cross-origin and CORS-blocked: the preview used to show
// the colours with no text (font-display: block) and then system fonts.
// inlineDocumentFonts swaps the font URLs for data: URIs (fetched here, in the
// app's own origin, once each, cached for the session) before the HTML goes
// into the frame — so the preview loads with no requests at all and its load
// event fires only once the fonts are in.
// ============================================================
import { documentFontsCss, needsLatinExt, type DocumentFontsCss } from './documentFonts'

const fontBase = (): string => `${window.location.origin}${process.env.PUBLIC_URL ?? ''}/document-fonts/`
const fontUrl = (file: string): string => `${fontBase()}${file}`

export const clientDocumentFonts = (headingFont: string | null | undefined, bodyFont: string | null | undefined): DocumentFontsCss =>
  documentFontsCss(headingFont, bodyFont, fontUrl)

// file → its data URI (a failed fetch is forgotten, so the next preview retries).
const dataUris = new Map<string, Promise<string | null>>()
const toBase64 = (buf: ArrayBuffer): string => {
  const bytes = new Uint8Array(buf)
  let bin = ''
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...Array.from(bytes.subarray(i, i + 0x8000)))
  return btoa(bin)
}
const dataUriOf = (file: string): Promise<string | null> => {
  const hit = dataUris.get(file)
  if (hit) return hit
  const p = Promise.resolve()
    .then(() => fetch(fontUrl(file)))
    .then(async (r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return `data:font/woff2;base64,${toBase64(await r.arrayBuffer())}`
    })
    .catch(() => {
      dataUris.delete(file)
      return null // that face stays a URL → the fallback stack, as before
    })
  dataUris.set(file, p)
  return p
}
// Only our own font files (a bare file name under the app's document-fonts).
const escapeRe = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
const visibleText = (html: string): string => html.replace(/<style[\s\S]*?<\/style>/gi, ' ').replace(/<[^>]*>/g, ' ')

// The document's HTML with its bundled-font URLs replaced by data: URIs. The
// Latin Extended files are inlined only when the text needs them (otherwise
// unicode-range never asks for them anyway). Never rejects.
export const inlineDocumentFonts = async (html: string): Promise<string> => {
  const re = new RegExp(`url\\("${escapeRe(fontBase())}([a-z0-9-]+\\.woff2)"\\)`, 'g')
  const ext = needsLatinExt(visibleText(html))
  const files = Array.from(new Set(Array.from(html.matchAll(re), (m) => m[1]))).filter((f) => ext || !f.includes('-latin-ext-'))
  if (!files.length) return html
  const uris = new Map<string, string | null>(await Promise.all(files.map(async (f) => [f, await dataUriOf(f)] as [string, string | null])))
  return html.replace(re, (whole, file: string) => {
    const uri = uris.get(file)
    return uri ? `url("${uri}")` : whole
  })
}

// Tests only: forget the cached data URIs.
export const resetDocumentFontCache = (): void => { dataUris.clear() }
